import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { 
  ClipboardCheck, CheckCircle, Circle, Calendar, Clock, 
  Loader2, AlertCircle, ChevronRight, Laptop, Package, 
  FileText, Battery, Camera, Award, MessageSquare, RotateCcw,
  UserCheck, UserX, Clock3, UserCog
} from 'lucide-react';
import { toast } from 'sonner';

const itemIcons = {
  'Laptops': Laptop,
  'Curriculum': FileText,
  'STEM Kits': Package,
  'Battery': Battery,
  'Certificates': Award,
  'T-shirts': Award,
  'Photo': Camera,
  'Feedback': MessageSquare,
  'Equipment': RotateCcw,
};

const getItemIcon = (itemName) => {
  for (const [key, Icon] of Object.entries(itemIcons)) {
    if (itemName.toLowerCase().includes(key.toLowerCase())) {
      return Icon;
    }
  }
  return Circle;
};

export default function InstructorChecklistPage() {
  const { api } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('all');
  const [loading, setLoading] = useState(true);
  const [togglingItem, setTogglingItem] = useState(null);
  
  // Attendance dialog state
  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState('present');
  const [attendanceNotes, setAttendanceNotes] = useState('');
  const [substituteName, setSubstituteName] = useState('');
  const [substituteInstructorId, setSubstituteInstructorId] = useState('');
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [markingAttendance, setMarkingAttendance] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get instructor's sections from instructor dashboard API
      const dashboardRes = await api.get('/instructor/dashboard');
      const sectionData = dashboardRes.data.sections || [];
      setSections(sectionData);

      // Get session checklists (my_checklists includes substitute assignments)
      const checklistsRes = await api.get('/checklists/sessions/my_checklists/');
      const checklistData = checklistsRes.data.results || checklistsRes.data || [];
      setChecklists(checklistData);
      
      // Get available instructors for substitute selection
      try {
        const instructorsRes = await api.get('/instructors/available/');
        setAvailableInstructors(instructorsRes.data || []);
      } catch (e) {
        console.log('Could not fetch available instructors');
      }
    } catch (error) {
      console.error('Failed to fetch checklists:', error);
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (checklistId, itemId, itemSource) => {
    setTogglingItem(`${checklistId}-${itemId}`);
    try {
      const response = await api.post(`/checklists/sessions/${checklistId}/toggle_item/`, {
        item_id: itemId,
        item_source: itemSource
      });
      
      // Update local state
      setChecklists(prev => prev.map(c => {
        if (c.id === checklistId) {
          const updatedCompletions = [...(c.item_completions || [])];
          const existingIdx = updatedCompletions.findIndex(
            ic => (ic.global_item === itemId || ic.course_item === itemId)
          );
          
          if (existingIdx >= 0) {
            updatedCompletions[existingIdx].is_completed = response.data.item_completed;
          } else {
            updatedCompletions.push({
              item_id: itemId,
              is_completed: response.data.item_completed
            });
          }
          
          return {
            ...c,
            is_completed: response.data.checklist_completed,
            completion_progress: response.data.progress,
            item_completions: updatedCompletions
          };
        }
        return c;
      }));

      if (response.data.checklist_completed) {
        toast.success('Checklist completed! Admin has been notified.');
      }
    } catch (error) {
      console.error('Failed to toggle item:', error);
      toast.error('Failed to update item');
    } finally {
      setTogglingItem(null);
    }
  };

  const handleCompleteAll = async (checklistId) => {
    try {
      await api.post(`/checklists/sessions/${checklistId}/complete_all/`);
      toast.success('All items marked as completed!');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete all items');
    }
  };

  const openAttendanceDialog = (checklist) => {
    setSelectedChecklist(checklist);
    setAttendanceStatus(checklist.instructor_attendance || 'present');
    setAttendanceNotes(checklist.attendance_notes || '');
    setSubstituteName(checklist.substitute_name || '');
    setSubstituteInstructorId(checklist.substitute_instructor_id || '');
    setAttendanceDialog(true);
  };

  const handleMarkAttendance = async () => {
    if (!selectedChecklist) return;
    
    setMarkingAttendance(true);
    try {
      await api.post(`/checklists/sessions/${selectedChecklist.id}/mark_attendance/`, {
        status: attendanceStatus,
        notes: attendanceNotes,
        substitute_name: attendanceStatus === 'substitute' ? substituteName : '',
        substitute_instructor_id: attendanceStatus === 'substitute' ? substituteInstructorId : null
      });
      
      const statusLabel = attendanceStatus === 'substitute' && substituteInstructorId 
        ? 'Substitute assigned and notified!' 
        : `Attendance marked as ${attendanceStatus}`;
      toast.success(statusLabel);
      setAttendanceDialog(false);
      fetchData();
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      toast.error('Failed to mark attendance');
    } finally {
      setMarkingAttendance(false);
    }
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'present': return <UserCheck className="h-4 w-4 text-green-600" />;
      case 'absent': return <UserX className="h-4 w-4 text-red-600" />;
      case 'late': return <Clock3 className="h-4 w-4 text-amber-600" />;
      case 'substitute': return <UserCog className="h-4 w-4 text-blue-600" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getAttendanceBadge = (status) => {
    const styles = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      late: 'bg-amber-100 text-amber-800',
      substitute: 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-600'
    };
    const labels = {
      present: 'Present',
      absent: 'Called Off',
      late: 'Late',
      substitute: 'Substitute',
      pending: 'Mark Attendance'
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {labels[status] || labels.pending}
      </Badge>
    );
  };

  const isItemCompleted = (checklist, itemId) => {
    const completions = checklist.item_completions || [];
    return completions.some(
      c => (c.item_id === itemId || c.global_item === itemId || c.course_item === itemId) && c.is_completed
    );
  };

  // Filter checklists
  const filteredChecklists = checklists.filter(c => 
    selectedSection === 'all' || c.section === selectedSection
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Stats
  const totalChecklists = checklists.length;
  const completedChecklists = checklists.filter(c => c.is_completed).length;
  const todayChecklists = checklists.filter(c => {
    const today = new Date().toISOString().split('T')[0];
    return c.session_date === today;
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" />
            Pre-Class Checklists
          </h1>
          <p className="text-muted-foreground">Complete your checklist before each session</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{todayChecklists}</p>
                <p className="text-sm text-muted-foreground">Today's Sessions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{totalChecklists - completedChecklists}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{completedChecklists}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={selectedSection} onValueChange={setSelectedSection}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="All Classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {sections.map(section => (
              <SelectItem key={section.id} value={section.id}>
                {section.course?.title || section.course_title || 'Course'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Checklists */}
      {filteredChecklists.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No Upcoming Checklists</h3>
            <p className="text-muted-foreground">
              Checklists will appear here when you have upcoming sessions
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredChecklists.map(checklist => (
            <Card key={checklist.id} className={`${checklist.is_completed ? 'border-green-200 bg-green-50/30' : ''} ${checklist.is_substitute_assignment ? 'ring-2 ring-blue-300' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {checklist.course_title}
                      {checklist.is_final_session && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">Final Session</Badge>
                      )}
                      {checklist.is_substitute_assignment && (
                        <Badge className="bg-blue-100 text-blue-800">
                          <UserCog className="h-3 w-3 mr-1" />
                          Substitute
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Session {checklist.session_number} • {formatDate(checklist.session_date)}
                      {checklist.is_substitute_assignment && checklist.original_instructor_name && (
                        <span className="ml-2 text-blue-600">• Covering for {checklist.original_instructor_name}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Attendance Button - only show for non-substitute assignments */}
                    {!checklist.is_substitute_assignment && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openAttendanceDialog(checklist)}
                        className={`flex items-center gap-2 ${
                          checklist.instructor_attendance === 'absent' ? 'border-red-300 bg-red-50' :
                          checklist.instructor_attendance === 'present' ? 'border-green-300 bg-green-50' :
                          checklist.instructor_attendance === 'late' ? 'border-amber-300 bg-amber-50' :
                          ''
                        }`}
                      >
                        {getAttendanceIcon(checklist.instructor_attendance)}
                        {getAttendanceBadge(checklist.instructor_attendance)}
                      </Button>
                    )}
                    {/* Progress */}
                    <div className="text-right">
                      <span className="text-lg font-bold">
                        {checklist.completion_progress?.completed || 0}/{checklist.completion_progress?.total || 0}
                      </span>
                      <p className="text-xs text-muted-foreground">items</p>
                    </div>
                    {checklist.is_completed ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Complete
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Checklist Items */}
                <div className="space-y-2">
                  {checklist.checklist_items?.map((item, idx) => {
                    const ItemIcon = getItemIcon(item.name);
                    const isCompleted = isItemCompleted(checklist, item.id);
                    const isToggling = togglingItem === `${checklist.id}-${item.id}`;
                    
                    return (
                      <div
                        key={`${item.id}-${idx}`}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-muted/50 ${
                          isCompleted ? 'bg-green-50 border-green-200' : 'bg-white'
                        }`}
                        onClick={() => !isToggling && handleToggleItem(checklist.id, item.id, item.source)}
                      >
                        {isToggling ? (
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        ) : (
                          <Checkbox 
                            checked={isCompleted}
                            className="h-5 w-5"
                          />
                        )}
                        <ItemIcon className={`h-5 w-5 ${isCompleted ? 'text-green-600' : 'text-muted-foreground'}`} />
                        <span className={`flex-1 ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                          {item.name}
                          {item.is_optional && <span className="text-muted-foreground text-sm"> (if needed)</span>}
                        </span>
                        {item.item_type === 'final_session' && (
                          <Badge variant="outline" className="text-xs">Final Session</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                {!checklist.is_completed && (
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleCompleteAll(checklist.id)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark All Complete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Attendance Dialog */}
      <Dialog open={attendanceDialog} onOpenChange={setAttendanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Your Attendance</DialogTitle>
          </DialogHeader>
          {selectedChecklist && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedChecklist.course_title}</p>
                <p className="text-sm text-muted-foreground">
                  Session {selectedChecklist.session_number} • {formatDate(selectedChecklist.session_date)}
                </p>
              </div>

              <div className="space-y-3">
                <Label>Attendance Status</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={attendanceStatus === 'present' ? 'default' : 'outline'}
                    className={`justify-start ${attendanceStatus === 'present' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setAttendanceStatus('present')}
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Present
                  </Button>
                  <Button
                    type="button"
                    variant={attendanceStatus === 'late' ? 'default' : 'outline'}
                    className={`justify-start ${attendanceStatus === 'late' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    onClick={() => setAttendanceStatus('late')}
                  >
                    <Clock3 className="h-4 w-4 mr-2" />
                    Late
                  </Button>
                  <Button
                    type="button"
                    variant={attendanceStatus === 'absent' ? 'default' : 'outline'}
                    className={`justify-start ${attendanceStatus === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    onClick={() => setAttendanceStatus('absent')}
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    Called Off
                  </Button>
                  <Button
                    type="button"
                    variant={attendanceStatus === 'substitute' ? 'default' : 'outline'}
                    className={`justify-start ${attendanceStatus === 'substitute' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    onClick={() => setAttendanceStatus('substitute')}
                  >
                    <UserCog className="h-4 w-4 mr-2" />
                    Substitute
                  </Button>
                </div>
              </div>

              {attendanceStatus === 'substitute' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Substitute Instructor</Label>
                    <Select value={substituteInstructorId || "none"} onValueChange={(val) => setSubstituteInstructorId(val === "none" ? "" : val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a registered instructor..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- None (enter name manually) --</SelectItem>
                        {availableInstructors.map(instructor => (
                          <SelectItem key={instructor.id} value={instructor.id}>
                            {instructor.name} ({instructor.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Selecting a registered instructor will notify them automatically
                    </p>
                  </div>
                  
                  {!substituteInstructorId && (
                    <div className="space-y-2">
                      <Label htmlFor="substitute_name">Or Enter Name Manually</Label>
                      <Input
                        id="substitute_name"
                        value={substituteName}
                        onChange={(e) => setSubstituteName(e.target.value)}
                        placeholder="Enter substitute instructor's name"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes for Substitute (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={attendanceNotes}
                      onChange={(e) => setAttendanceNotes(e.target.value)}
                      placeholder="Any special instructions for the substitute..."
                      rows={2}
                    />
                  </div>

                  {substituteInstructorId && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <UserCog className="h-4 w-4 inline mr-1" />
                        The selected instructor will receive a notification and can complete the checklist under their account.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {(attendanceStatus === 'absent' || attendanceStatus === 'late') && (
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={attendanceNotes}
                    onChange={(e) => setAttendanceNotes(e.target.value)}
                    placeholder="Reason for absence or late arrival..."
                    rows={2}
                  />
                </div>
              )}

              {attendanceStatus === 'absent' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    Marking as "Called Off" will notify admin. Please ensure you've communicated with your supervisor.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttendanceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkAttendance} 
              disabled={markingAttendance || (attendanceStatus === 'substitute' && !substituteInstructorId && !substituteName.trim())}
            >
              {markingAttendance && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {attendanceStatus === 'substitute' && substituteInstructorId ? 'Assign & Notify Substitute' : 'Save Attendance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
